/**
 * LockStatusBanner Component
 * 
 * Visual indicator showing when an item is locked by another user.
 * Displays:
 * - Who is editing (user name + avatar)
 * - "Read-only" badge
 * - Lock status and time
 */

import React, { useState } from 'react';
import { Lock, Eye, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { LockStatus } from '@/lib/stores/itemLockStore';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { ForceLockDialog } from './ForceLockDialog';

export interface LockStatusBannerProps {
  /** Lock status from useItemLock hook */
  lockStatus: LockStatus;
  /** Whether lock is being acquired */
  isAcquiring?: boolean;
  /** Error message if lock acquisition failed */
  lockError?: string | null;
  /** Whether the current user can edit */
  canEdit?: boolean;
  /** Callback to refresh lock status */
  onRefresh?: () => void;
  /** Callback to force takeover the lock */
  onForceTakeover?: () => Promise<void>;
  /** Whether the current user can force takeover (based on permissions) */
  canForceTakeover?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show in compact mode (just badge) */
  compact?: boolean;
}

export function LockStatusBanner({
  lockStatus,
  isAcquiring = false,
  lockError,
  canEdit = true,
  onRefresh,
  onForceTakeover,
  canForceTakeover = false,
  className,
  size = 'md',
  compact = false,
}: LockStatusBannerProps) {
  const { t } = usePlatformTranslation();
  const [showForceDialog, setShowForceDialog] = useState(false);
  const [isForcingTakeover, setIsForcingTakeover] = useState(false);

  const handleForceTakeover = async () => {
    if (!onForceTakeover) return;
    
    setIsForcingTakeover(true);
    try {
      await onForceTakeover();
    } finally {
      setIsForcingTakeover(false);
    }
  };

  // Don't render anything if not locked or if user owns the lock
  if (!lockStatus.isLocked || lockStatus.isOwnLock) {
    return null;
  }

  // Format the locked time
  const formatLockedTime = () => {
    if (!lockStatus.lockedAt) return '';
    
    const lockedDate = new Date(lockStatus.lockedAt);
    const now = new Date();
    const diffMs = now.getTime() - lockedDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return t('common.justNow', 'just now');
    } else if (diffMins < 60) {
      return t('common.minutesAgo', '{{count}} min ago', { count: diffMins });
    } else {
      const diffHours = Math.floor(diffMins / 60);
      return t('common.hoursAgo', '{{count}}h ago', { count: diffHours });
    }
  };

  // Get initials for avatar fallback
  const getInitials = (name: string | null) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Size-based styles
  const sizeStyles = {
    sm: {
      banner: 'py-1.5 px-3 gap-2 text-xs',
      avatar: 'w-5 h-5 text-[10px]',
      icon: 'w-3 h-3',
    },
    md: {
      banner: 'py-2 px-4 gap-3 text-sm',
      avatar: 'w-7 h-7 text-xs',
      icon: 'w-4 h-4',
    },
    lg: {
      banner: 'py-3 px-5 gap-4 text-base',
      avatar: 'w-9 h-9 text-sm',
      icon: 'w-5 h-5',
    },
  };

  const styles = sizeStyles[size];

  // Compact mode - just show a badge
  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="secondary" 
              className={cn(
                'gap-1 cursor-help bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/30',
                className
              )}
            >
              <Lock className="w-3 h-3" />
              <span>{t('common.readOnly', 'Read-only')}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="flex flex-col gap-1">
              <span className="font-medium">
                {t('lock.editingBy', 'Being edited by {{name}}', { name: lockStatus.lockedByName || 'Unknown' })}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('lock.startedEditing', 'Started {{time}}', { time: formatLockedTime() })}
              </span>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Full banner mode
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg',
        'bg-amber-50 dark:bg-amber-950/30',
        'border border-amber-200 dark:border-amber-800',
        styles.banner,
        className
      )}
    >
      {/* Left side - Lock info */}
      <div className="flex items-center gap-3">
        {/* Lock icon */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50">
          <Lock className={cn(styles.icon, 'text-amber-600 dark:text-amber-400')} />
        </div>

        {/* User info */}
        <div className="flex items-center gap-2">
          {/* Avatar */}
          {lockStatus.lockedByAvatar ? (
            <img
              src={lockStatus.lockedByAvatar}
              alt={lockStatus.lockedByName || 'User'}
              className={cn(styles.avatar, 'rounded-full object-cover')}
            />
          ) : (
            <div
              className={cn(
                styles.avatar,
                'rounded-full flex items-center justify-center',
                'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 font-medium'
              )}
            >
              {getInitials(lockStatus.lockedByName)}
            </div>
          )}

          {/* Name and status */}
          <div className="flex flex-col">
            <span className="font-medium text-amber-900 dark:text-amber-100">
              {lockStatus.lockedByName || t('common.unknownUser', 'Unknown User')}
            </span>
            <span className="text-amber-700 dark:text-amber-400 text-xs">
              {t('lock.isEditing', 'is editing')} · {formatLockedTime()}
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Read-only badge and actions */}
      <div className="flex items-center gap-2">
        <Badge 
          variant="secondary" 
          className="gap-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 border-0"
        >
          <Eye className="w-3 h-3" />
          <span>{t('common.readOnly', 'Read-only')}</span>
        </Badge>

        {/* Force takeover button */}
        {canForceTakeover && onForceTakeover && (
          <>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowForceDialog(true)}
              disabled={isForcingTakeover || isAcquiring}
              className="h-7"
            >
              {t('lock.takeOver', 'Take Over')}
            </Button>
            
            <ForceLockDialog
              open={showForceDialog}
              onOpenChange={setShowForceDialog}
              lockedByName={lockStatus.lockedByName || t('common.unknownUser', 'Unknown User')}
              onConfirm={handleForceTakeover}
              isLoading={isForcingTakeover}
            />
          </>
        )}

        {/* Refresh button */}
        {onRefresh && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isAcquiring}
                  className="h-7 w-7 p-0 text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200 hover:bg-amber-200/50 dark:hover:bg-amber-800/50"
                >
                  <RefreshCw className={cn('w-4 h-4', isAcquiring && 'animate-spin')} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('lock.refreshStatus', 'Refresh lock status')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

/**
 * Inline lock indicator for headers/titles
 */
export function LockIndicator({
  lockStatus,
  size = 'sm',
  className,
}: {
  lockStatus: LockStatus;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { t } = usePlatformTranslation();

  if (!lockStatus.isLocked || lockStatus.isOwnLock) {
    return null;
  }

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex items-center', className)}>
            <Lock className={cn(iconSize, 'text-amber-500')} />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('lock.lockedBy', 'Locked by {{name}}', { name: lockStatus.lockedByName || 'Unknown' })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default LockStatusBanner;

